// AccountSessionContext — the only UI source of "am I logged in".
// Reads GET /v1/me. A local flag or leftover localStorage token is not truth.

import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ApiError } from '../api/client'
import {
  type MeSnapshot,
  clearSessionToken,
  fetchMe,
  getSessionToken,
  logoutSession,
  onSessionChanged,
} from '../api/userland'
import { claimAccountCaches, resetAccountClientState } from './accountReset'

export type AccountSessionStatus = 'loading' | 'guest' | 'ready'

export type AccountSessionValue = {
  status: AccountSessionStatus
  me: MeSnapshot | null
  reload: () => Promise<void>
  logout: () => Promise<void>
}

const Ctx = createContext<AccountSessionValue | null>(null)
const ME_SNAPSHOT_KEY = 'wils.me.snapshot'
// A failed read that is not a 401 is asked again: 1 s, doubling, at most 30 s.
const RETRY_FIRST_MS = 1_000
const RETRY_MAX_MS = 30_000

function readRememberedMe(): MeSnapshot | null {
  if (typeof sessionStorage === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(ME_SNAPSHOT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<MeSnapshot>
    if (parsed.authenticated !== true || !parsed.account_id || !parsed.session_id) return null
    return parsed as MeSnapshot
  } catch {
    return null
  }
}

function writeRememberedMe(snapshot: MeSnapshot | null): void {
  if (typeof sessionStorage === 'undefined') return
  try {
    if (!snapshot) sessionStorage.removeItem(ME_SNAPSHOT_KEY)
    else sessionStorage.setItem(ME_SNAPSHOT_KEY, JSON.stringify(snapshot))
  } catch {
    // storage unavailable
  }
}

export function AccountSessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AccountSessionStatus>(() =>
    readRememberedMe() ? 'ready' : 'loading'
  )
  const [me, setMe] = useState<MeSnapshot | null>(() => readRememberedMe())
  // Set while this provider ends the session itself (Log out, a refused
  // session): the session event it raises then is its own.
  const quiet = useRef(false)
  // Bumped by Log out: a /v1/me read started before it is not trusted after.
  const readSeq = useRef(0)
  // Whether this tab has had a signed-in account since the last reset.
  const hadAccount = useRef(Boolean(readRememberedMe()))
  const retry = useRef<{ timer: ReturnType<typeof setTimeout> | null; attempt: number }>({
    timer: null,
    attempt: 0,
  })

  const reload = useMemo(() => {
    const run = async (): Promise<void> => {
      if (retry.current.timer !== null) {
        clearTimeout(retry.current.timer)
        retry.current.timer = null
      }
      const seq = readSeq.current
      try {
        const snapshot = await fetchMe()
        if (seq !== readSeq.current) return
        // Another account than the one whose caches this browser holds: they
        // go before anything of the new account is drawn or merged.
        await claimAccountCaches(snapshot.account_id)
        if (seq !== readSeq.current) return
        retry.current.attempt = 0
        hadAccount.current = true
        writeRememberedMe(snapshot)
        setMe(snapshot)
        setStatus('ready')
      } catch (err) {
        if (seq !== readSeq.current) return
        if (err instanceof ApiError && err.status === 401) {
          retry.current.attempt = 0
          writeRememberedMe(null)
          setMe(null)
          setStatus('guest')
          // A signed-in session refused (expired, revoked by a password
          // change elsewhere): the account left this tab as surely as by
          // Log out, so its data goes too (not a sign-in in progress here).
          if (hadAccount.current) {
            hadAccount.current = false
            void resetAccountClientState({ signInFlows: false })
          }
          // End the binding too, so the vault drops the account's keyring.
          if (getSessionToken() !== null) {
            quiet.current = true
            try {
              clearSessionToken()
            } finally {
              quiet.current = false
            }
          }
          return
        }
        // A network error or a 5xx (an API roll) says nothing about the
        // session: a remembered one stays ready, and the read is asked again,
        // so one failed read does not turn a logged-in user into a guest until
        // a manual reload. With nothing remembered nothing proves a session:
        // the page shows a guest (Log in, not Deposit) until the API answers.
        setStatus((current) => (current === 'loading' ? 'guest' : current))
        const delay = Math.min(RETRY_MAX_MS, RETRY_FIRST_MS * 2 ** retry.current.attempt)
        retry.current.attempt += 1
        retry.current.timer = setTimeout(() => void run(), delay)
      }
    }
    return run
  }, [])

  useEffect(() => {
    void reload()
    const stop = onSessionChanged(() => {
      if (quiet.current) return
      setStatus((current) => (current === 'guest' ? 'loading' : current))
      void reload()
    })
    return () => {
      stop()
      if (retry.current.timer !== null) clearTimeout(retry.current.timer)
      retry.current.timer = null
    }
  }, [reload])

  const value = useMemo<AccountSessionValue>(
    () => ({
      status,
      me,
      reload,
      // The page is a guest the moment Log out is pressed: the account's
      // client state goes first (no frame shows it after the click), the
      // server's answer is not waited for. Then the session is read again:
      // if the server did not end it, the page says so instead of
      // pretending to be a guest.
      logout: async () => {
        readSeq.current += 1
        quiet.current = true
        if (retry.current.timer !== null) clearTimeout(retry.current.timer)
        retry.current.timer = null
        writeRememberedMe(null)
        setMe(null)
        setStatus('guest')
        hadAccount.current = false
        try {
          await resetAccountClientState({ signInFlows: true })
          await logoutSession()
          // A limit-order reload that was mid-flight under the old cookie may
          // have written rows back meanwhile.
          await resetAccountClientState({ signInFlows: false })
        } finally {
          quiet.current = false
        }
        void reload()
      },
    }),
    [me, reload, status]
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAccountSession(): AccountSessionValue {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useAccountSession must be used inside <AccountSessionProvider>')
  return ctx
}

export function useIsGuest(): boolean {
  return useAccountSession().status === 'guest'
}

export function useOptionalAccountSession(): AccountSessionValue | null {
  return useContext(Ctx)
}
